import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, Instagram, AlertCircle, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { humanizeSupabaseError } from "@/lib/supabase-errors";

interface PageOption {
  page_id: string;
  page_name: string;
  ig_user_id: string;
  ig_username: string | null;
  ig_profile_pic: string | null;
}

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; pages: PageOption[] }
  | { kind: "error"; message: string; code?: number };

export default function OAuthSelectPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const sessionId = searchParams.get("session");

  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [submitting, setSubmitting] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) {
      setState({ kind: "error", message: "Sessão inválida ou ausente." });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("instagram-oauth-finalize", {
          method: "GET" as any,
          // supabase-js v2 doesn't accept `query` for invoke; we manually use fetch fallback
        });
        // Fallback: manual fetch with query string (invoke doesn't support GET query in all versions)
        if (error || !data) {
          const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
          const url = `https://${projectId}.supabase.co/functions/v1/instagram-oauth-finalize?session=${encodeURIComponent(sessionId)}`;
          const { data: { session } } = await supabase.auth.getSession();
          const r = await fetch(url, {
            headers: {
              Authorization: `Bearer ${session?.access_token ?? ""}`,
              apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            },
          });
          const json = await r.json();
          if (cancelled) return;
          if (!r.ok) {
            setState({
              kind: "error",
              message: humanizeSupabaseError(json?.error, {
                fallback: "Falha ao carregar sessão.",
              }),
              code: r.status,
            });
            return;
          }
          setState({ kind: "ready", pages: (json.pages || []) as PageOption[] });
          return;
        }
        if (cancelled) return;
        setState({ kind: "ready", pages: (data.pages || []) as PageOption[] });
      } catch (err) {
        if (cancelled) return;
        setState({
          kind: "error",
          message: humanizeSupabaseError(err, { fallback: "Erro inesperado." }),
        });
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  async function handleChoose(pageId: string) {
    if (!sessionId || submitting) return;
    setSubmitting(pageId);
    try {
      const { data, error } = await supabase.functions.invoke("instagram-oauth-finalize", {
        body: { session: sessionId, page_id: pageId },
      });
      if (error) {
        const status = (error as any).context?.status;
        let message = error.message || "Falha ao conectar.";
        if (status === 409) message = "Esta Page ou conta Instagram já está ativa em outro workspace.";
        if (status === 410) message = "Esta sessão expirou ou já foi utilizada. Reinicie a conexão.";
        if (status === 403) message = "Você não tem permissão neste workspace.";
        if (status === 502) message = "Não foi possível revalidar a Page com o Instagram. Reinicie o OAuth.";
        toast({ title: "Erro", description: message, variant: "destructive" });
        setSubmitting(null);
        return;
      }
      toast({
        title: "Conta conectada",
        description: data?.ig_username ? `@${data.ig_username} conectado com sucesso.` : "Integração concluída.",
      });
      navigate(`/oauth/instagram?success=1&username=${encodeURIComponent(data?.ig_username ?? "")}`);
    } catch (err) {
      toast({ title: "Erro", description: err instanceof Error ? err.message : "Erro inesperado.", variant: "destructive" });
      setSubmitting(null);
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-primary/10 p-2">
              <Instagram className="w-6 h-6 text-primary" />
            </div>
            <div>
              <CardTitle>Selecione a conta do Instagram</CardTitle>
              <CardDescription>
                Várias contas foram autorizadas. Escolha qual conectar a este workspace.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {state.kind === "loading" && (
            <>
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </>
          )}

          {state.kind === "error" && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Não foi possível continuar</AlertTitle>
              <AlertDescription className="space-y-3">
                <p>{state.message}</p>
                <Button variant="outline" size="sm" onClick={() => navigate("/integrations")}>
                  Voltar para Integrações
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {state.kind === "ready" && state.pages.length === 0 && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>Nenhuma conta Instagram disponível nesta sessão.</AlertDescription>
            </Alert>
          )}

          {state.kind === "ready" && state.pages.map((p) => {
            const isLoading = submitting === p.page_id;
            const disabled = !!submitting;
            return (
              <button
                key={p.page_id}
                onClick={() => handleChoose(p.page_id)}
                disabled={disabled}
                className="w-full flex items-center gap-4 p-4 rounded-lg border bg-card hover:bg-accent transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Avatar className="w-12 h-12">
                  {p.ig_profile_pic && <AvatarImage src={p.ig_profile_pic} alt={p.ig_username ?? ""} />}
                  <AvatarFallback>
                    <Instagram className="w-5 h-5" />
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {p.ig_username ? `@${p.ig_username}` : p.ig_user_id}
                  </div>
                  <div className="text-sm text-muted-foreground truncate">
                    Page do Facebook: {p.page_name}
                  </div>
                </div>
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                ) : (
                  <CheckCircle2 className="w-5 h-5 text-muted-foreground" />
                )}
              </button>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
